import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import VideoGuideSection from './VideoGuideSection';
import { Box } from '@mui/material';

const UserGuide = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [openFaqItem, setOpenFaqItem] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const resizeTimeoutRef = useRef(null);

  // Guide images data
  const guideImages = {
    addItemStep1: '/images/guide/add_item_step_1.png',
    addItemStep2: '/images/guide/add_item_step_2.png',
    createNewProject: '/images/guide/create_new_project.png',
    itemWithoutResources: '/images/guide/item_without_resrouces.png',
    projectDetails: '/images/guide/project_details.png',
    saveNewChapter: '/images/guide/save_new_chapter.png'
  };

  const styles = {
    paragraph: {
      textAlign: 'right'
    }
  };
  
  // Video guide data
  const videoGuideData = [
    {
      id: 1,
      title: "מדריך התחלה מהירה לקוסטינג ויזארד",
      duration: "05:16",
      videoId: "NDb5ASZ2t2c"
    },
    {
      id: 2,
      title: "בנה הצעת מחיר עם AI בשלוש דקות",
      duration: "03:00",
      videoId: "IfW1-MRIDHY"
    },
    {
      id: 3,
      title: "חדש ב־CostingWizard: ייבוא חשבוניות והצעות מחיר מאקסל בעזרת AI!",
      duration: "5:39",
      videoId: "JeJJ4RmI87c"
    },
    {
      id: 4,
      title: "הסבר על פרויקט לדוגמה בקוסטינג ויזארד",
      duration: "04:06",
      videoId: "2HUjQRB3aIM"
    },
    {
      id: 5,
      title: "איך לשנות מטבע",
      duration: "0:52", 
      videoId: "6FtXdSnRmBA"
    }
  ];

  // Initialize with first video
  React.useEffect(() => {
    if (videoGuideData.length > 0 && !currentVideo) {
      setCurrentVideo(videoGuideData[0]);
    }
  }, [videoGuideData, currentVideo]);

  const handleVideoClick = (video) => {
    setCurrentVideo(video);
  };

  const handleShowAllVideos = () => {
    window.open('https://www.youtube.com/watch?v=DtMMp_dFQGc&list=PLRpCe7B0Fq5qTXH5zJJ5Q30SPJPIDRPr5', '_blank', 'noopener,noreferrer');
  };

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      // Force a re-render of components that might need resizing
      setIsNavOpen(prev => prev);
    }, 300);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [handleResize]);

  const sectionsData = useMemo(() => [
    {
      id: 'h2-overview',
      title: '🏗️ סקירה כללית',
      keywords: ['סקירה כללית', 'מה זה', 'תכונות', 'יכולות', 'חישוב עלויות', 'הצעות מחיר', 'AI', 'בינה מלאכותית', 'ייצוא PDF', 'Excel'],
      searchableContent: "Costing Wizard הוא כלי מתקדם המיועד לאומני מקצוע, קבלנים ויזמים ליצירת הצעות מחיר מדויקות ומקצועיות. המערכת משלבת חישובי עלויות חכמים, ניהול פרויקטים ובינה מלאכותית לחוויית משתמש מושלמת. תכונות מרכזיות: חישוב עלויות אוטומטי - מחשבון מתקדם לכל סוגי העבודות, תבניות מותאמות אישית - שמירת תבניות למשימות חוזרות, בינה מלאכותית - יצירת פרויקטים בעזרת AI, ייצוא מקצועי - PDF ו-Excel מעוצבים, גיבוי בענן - שמירה אוטומטית ובטוחה, גישה ממכשירים - עובד באנדרואיד ואייפון. מושלם עבור: קבלנים, אומני מקצוע, מתכננים, יועצים וכל מי שזקוק לכלי מקצועי לחישוב עלויות והצעות מחיר."
    },
    {
      id: 'h2-videos',
      title: '📺 סרטוני הדרכה',
      keywords: ['סרטוני הדרכה', 'וידאו', 'הדגמה', 'מדריך וידאו', 'צפייה', 'למידה חזותית', 'הסברים', 'דוגמאות', 'הדרכה מוחשית', 'מדריך התחלה מהירה', 'quick start'],
      searchableContent: "סרטוני הדרכה מפורטים המדגימים איך להשתמש בכל התכונות של CostingWizard. הדרכות וידאו לכל השלבים: יצירת פרויקט, הוספת פרקים וסעיפים, שימוש ב-AI, ייצוא PDF ו-Excel, ניהול מנוי. למידה חזותית ומעשית של המערכת. מדריך התחלה מהירה."
    },
    {
      id: 'h2-getting-started-main',
      title: '🚀 תחילת עבודה',
      keywords: ['תחילת עבודה', 'התחלה', 'הרשמה', 'כניסה', 'הורדה', 'אפליקציה', 'app store', 'google play', 'אימייל', 'פרויקט ראשון', 'יצירת פרויקט', 'מסך רשימת פרויקטים', 'הוסף פרויקט חדש', 'הוסף פרויקט עם AI'],
      searchableContent: "צעדים ראשונים. תהליך הרשמה וכניסה למערכת: הורד את האפליקציה מ-App Store או Google Play. היכנס לכתובת costingwizard.co.il או לחץ הרשמה באפליקציה. לחץ על הרשמה וצור חשבון חדש. אמת את כתובת האימייל שלך. התחבר דרך האפליקציה. צור את הפרויקט הראשון שלך! כל המשתמשים מקבלים גישה חינמית לכל התכונות עם מגבלות בסיסיות. ניתן לשדרג למנוי פרימיום או אנטרפרייז בכל עת. יצירת פרויקט ראשון. מסך רשימת פרויקטים: ברוכים הבאים לאפליקציית CostingWizard! במסך זה תוכלו לראות את כל הפרויקטים שכבר יצרתם. כל שורה בטבלה מייצגת פרויקט וכוללת את הפרטים הבאים: תאריך, מזהה, תיאור. יצירת פרויקט חדש: הוסף פרויקט חדש או הוסף פרויקט עם AI."
    },
    {
      id: 'h2-add-chapters-items',
      title: '🧱 הוספת פרקים וסעיפים לפרויקט',
      keywords: ['הוספת פרקים', 'סעיפים', 'פרקים', 'תקציב', 'תחומים', 'משימות'],
      searchableContent: "לאחר שיצרתם פרויקט חדש, תבנו את התקציב שלו על ידי הוספת פרקים (תחומים/שלבים עיקריים) ולכל פרק – סעיפים (משימות מפורטות). פרק הוא קבוצה של עבודות מאותו תחום. סעיף הוא משימה או רכיב ספציפי בתוך פרק."
    },
    {
      id: 'h2-add-location-quantities',
      title: '📍 הוספת מיקום וכמויות לסעיף',
      keywords: ['מיקום', 'כמות', 'מכפילים', 'משאבים', 'סעיף'],
      searchableContent: "בשלב זה תוכלו להכניס את המיקום של הסעיף בפרויקט ואת הכמות בפועל, הנקבעת לפי ארבעה מכפילים. האפליקציה מחשבת את הכמות הכוללת של הסעיף כך: כמות = מכפיל 1 × מכפיל 2 × מכפיל 3 × מכפיל 4. זוהי דרך גמישה ונוחה לחשב כמויות על בסיס מידות או מרכיבים שונים."
    },
    {
      id: 'h2-dashboard',
      title: '📊 לוח הבקרה',
      keywords: ['לוח הבקרה', 'פיקוד', 'סטטיסטיקות', 'ניהול', 'חשבון'],
      searchableContent: "לוח הבקרה הוא מרכז הפיקוד שלך. כאן תמצא את כל הפרויקטים, סטטיסטיקות שימוש ואפשרויות ניהול החשבון. פרטי מנוי: מידע אישי ופרטי חשבון, סטטוס מנוי נוכחי, הגדרות מטבע. נתוני שימוש: מונה ייצואי PDF ו-Excel החודש, שימוש בבינה מלאכותית, מספר פרויקטים שמורים."
    },
    {
      id: 'h2-ai-features',
      title: '🤖 בינה מלאכותית',
      keywords: ['בינה מלאכותית', 'AI', 'יצירת פרויקט', 'פריטים', 'מבנה', 'תיאור'],
      searchableContent: "התכונה החדשנית שלנו מאפשרת יצירת פרויקטים באמצעות בינה מלאכותית. פשוט תאר מה אתה רוצה לבנות והמערכת תיצור עבורך רשימת פריטים מפורטת. איך להשתמש ב-AI: כתב מה אתה רוצה לבנות או לשפץ, ציין גדלים וכמויות אם יש לך, ציין סוג עבודה, בדוק התוצאה."
    },
    {
      id: 'h2-advanced-features',
      title: '✨ תכונות מתקדמות',
      keywords: ['תכונות מתקדמות', 'שכפול פרויקטים', 'חישוב אוטומטי', 'שכפול פרקים'],
      searchableContent: "שכפול פרויקטים: העתק פרויקט קיים כבסיס לחדש. חישוב אוטומטי: עדכון סה\"כ אוטומטי בשינוי כמויות. שכפול פרקים מפרויקט אחר: העתק פרקים שלמים ממקורות אחרים לפרויקט הנוכחי."
    },
    {
      id: 'h2-export-share',
      title: '📄 ייצוא ושיתוף',
      keywords: ['ייצוא', 'שיתוף', 'PDF', 'Excel', 'פורמטים', 'מקצועיים'],
      searchableContent: "לאחר יצירת הפרויקט, תוכל לייצא אותו במגוון פורמטים מקצועיים ולשתף עם לקוחות ושותפים. ייצוא PDF: הפקת הצעת מחיר מעוצבת ומסודרת. ייצוא Excel: ייצוא כל הפרויקט לקובץ Excel מפורט – כולל כל הפרקים, הסעיפים והמשאבים."
    },
    {
      id: 'h2-subscription',
      title: '💳 ניהול מנוי',
      keywords: ['ניהול מנוי', 'חבילות', 'מנויים', 'פרילאנסר', 'חברה', 'גדולה'],
      searchableContent: "המערכת מציעה מספר חבילות מנוי להתאמה לכל סוג של משתמש - מפרילאנסר יחיד ועד חברה גדולה. חבילות מנוי זמינות: חינם: עד 2 פרויקטים, עד 3 ייצוא PDF/Excel, 2 קריאות בינה מלאכותית. Premium: עד 8 פרויקטים, עד 20 ייצוא PDF/Excel, 10 קריאות AI."
    }
  ], []);

  const performSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = sectionsData.flatMap(section => {
      const sectionMatches = section.keywords.some(keyword => 
        keyword.toLowerCase().includes(query.toLowerCase())
      ) || section.searchableContent.toLowerCase().includes(query.toLowerCase());
      
      return sectionMatches ? [{ ...section, type: 'section' }] : [];
    });

    setSearchResults(results.slice(0, 5));
  }, [sectionsData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => performSearch(searchQuery), 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const filteredResults = useMemo(() => {
    if (!searchQuery) return searchResults;
    return searchResults.filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, searchResults]);

  const handleFaqClick = (index) => {
    setOpenFaqItem(openFaqItem === index ? null : index);
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif', direction: 'rtl' }}>
      <style>
        {`
          .doc-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
            min-height: 100vh;
          }
          .doc-header {
            background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
            color: white;
            padding: 60px 40px;
            text-align: center;
          }
          .doc-header h1 {
            margin: 0;
            font-size: 2.5em;
            font-weight: 300;
          }
          .doc-header p {
            margin: 10px 0 0 0;
            font-size: 1.2em;
            opacity: 0.9;
          }
          .search-container {
            background: #f8f9fa;
            padding: 20px;
            border-bottom: 1px solid #dee2e6;
          }
          .search-box {
            max-width: 500px;
            margin: 0 auto;
            position: relative;
          }
          .search-input {
            width: 100%;
            padding: 12px 45px 12px 20px;
            border: 2px solid #1976d2;
            border-radius: 25px;
            font-size: 16px;
            outline: none;
            direction: rtl;
            font-family: inherit;
          }
          .search-input:focus {
            box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
          }
          .search-icon {
            position: absolute;
            left: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: #666;
          }
          .search-results {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            z-index: 1100;
            max-height: 300px;
            overflow-y: auto;
          }
          .search-result-item {
            padding: 12px 20px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
            transition: background 0.2s;
          }
          .search-result-item:hover {
            background: #f8f9fa;
          }
          .search-result-item:last-child {
            border-bottom: none;
          }
          .doc-nav {
            background: #2c3e50;
            position: sticky;
            top: 0;
            z-index: 1000;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
          }
          .nav-header {
            display: none;
            padding: 15px 20px;
            background: #34495e;
            color: white;
            justify-content: space-between;
            align-items: center;
          }
          .nav-toggle {
            background: none;
            border: none;
            color: white;
            font-size: 1.5em;
            cursor: pointer;
            padding: 5px;
          }
          .doc-nav ul {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
          }
          .doc-nav li {
            margin: 0;
          }
          .doc-nav a {
            display: block;
            padding: 15px 20px;
            color: white;
            text-decoration: none;
            transition: background 0.3s;
            cursor: pointer;
            white-space: nowrap;
          }
          .doc-nav a:hover {
            background: #34495e;
          }
          .doc-content {
            padding: 40px;
            line-height: 1.6;
            color: #333;
          }
          .section {
            margin-bottom: 60px;
          }
          .section h2 {
            color: #2c3e50;
            font-size: 2em;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #1976d2;
            scroll-margin-top: 90px;
          }
          .section h3 {
            color: #34495e;
            font-size: 1.5em;
            margin: 30px 0 15px 0;
          }
          .section h4 {
            color: #34495e;
            font-size: 1.3em;
            margin: 24px 0 12px 0;
            padding-bottom: 10px;
            border-bottom: 1px solid #1976d2;
          }
          .section h5 {
            font-size: 1.3em;
            font-weight: bold;
            margin: 24px 0 12px 0;
            color: #34495e;
          }
          .section h6 {
            font-size: 1.1em;
            font-weight: bold;
            margin: 24px 0 12px 0;
            color: #34495e;
          }
          .section p {
            margin: 15px 0;
            line-height: 1.8;
          }
          .feature-list {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
          }
          .feature-list ul {
            margin: 10px 0;
            padding-right: 30px;
          }
          .feature-list li {
            margin: 8px 0;
          }
          .highlight-box {
            background: #e8f4f8;
            border-right: 4px solid #1976d2;
            padding: 15px 20px;
            margin: 20px 0;
          }
          .warning-box {
            background: #fff3cd;
            border-right: 4px solid #ffc107;
            padding: 15px 20px;
            margin: 20px 0;
          }
          .success-box {
            background: #d4edda;
            border-right: 4px solid #28a745;
            padding: 15px 20px;
            margin: 20px 0;
          }
          @media (max-width: 768px) {
            .doc-content {
              padding: 20px;
            }
            .doc-header {
              padding: 40px 20px;
            }
            .doc-header h1 {
              font-size: 1.8em;
            }
            .nav-header {
              display: flex;
            }
            .doc-nav ul {
              display: ${isNavOpen ? 'flex' : 'none'};
              flex-direction: column;
            }
            .doc-nav li {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="doc-container">
        <header className="doc-header">
          <h1>Costing Wizard - מדריך למתחילים</h1>
          <p>למד איך להשתמש ב-CostingWizard בצורה מקצועית וחכמה</p>
        </header>

        <div className="search-container">
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="חפש במדריך..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="search-icon">🔍</div>
            
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="search-result-item"
                    onClick={() => {
                      document.getElementById(result.id)?.scrollIntoView({ behavior: 'smooth' });
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <strong>{result.title}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <nav className="doc-nav">
          <div className="nav-header">
            <span>תוכן עניינים</span>
            <button className="nav-toggle" onClick={() => setIsNavOpen(!isNavOpen)}>
              ☰
            </button>
          </div>
          <ul>
            {sectionsData.map(section => (
              <li key={section.id}>
                <a onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}>
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="doc-content">
          {/* Overview Section */}
          <section id="h2-overview" className="section">
            <h2 id="h2-overview">🏗️ סקירה כללית</h2>
            <p>Costing Wizard הוא כלי מתקדם המיועד לאומני מקצוע, קבלנים ויזמים ליצירת הצעות מחיר מדויקות ומקצועיות.</p>
            
            <div className="feature-list">
              <h3>🌟 תכונות מרכזיות</h3>
              <ul>
                <li><strong>חישוב עלויות אוטומטי</strong> - מחשבון מתקדם לכל סוגי העבודות</li>
                <li><strong>תבניות מותאמות אישית</strong> - שמירת תבניות למשימות חוזרות</li>
                <li><strong>בינה מלאכותית</strong> - יצירת פרויקטים בעזרת AI</li>
                <li><strong>ייצוא מקצועי</strong> - PDF ו-Excel מעוצבים</li>
                <li><strong>גיבוי בענן</strong> - שמירה אוטומטית ובטוחה</li>
                <li><strong>גישה ממכשירים</strong> - עובד באנדרואיד ואייפון</li>
              </ul>
            </div>
            
            <div className="highlight-box">
              <strong>מושלם עבור:</strong> קבלנים, אומני מקצוע, מתכננים, יועצים וכל מי שזקוק לכלי מקצועי לחישוב עלויות והצעות מחיר.
            </div>
          </section>

          {/* Videos Section */}
          <section id="h2-videos" className="section">
            <h2 id="h2-videos">📺 סרטוני הדרכה</h2>
            <p>למד איך להשתמש ב-CostingWizard דרך סרטוני הדרכה מפורטים. הסרטונים מדגימים כל תכונה בצורה חזותית וברורה.</p>
            
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',                    // Mobile: Stack vertically
                  sm: '1fr',                    // Small tablets: Still stack
                  md: '1fr 350px',             // Desktop: Video left, sidebar right
                  lg: '1fr 400px',             // Large desktop: Optimal size
                },
                gap: { xs: 2, sm: 3, md: 4 },  // Responsive gap
                mt: 3,
                mb: 3,
                px: { xs: 1, sm: 0 }           // Padding on mobile
              }}
            >
              {/* Embedded Video Player */}
              <Box sx={{ position: 'relative', order: { xs: 2, md: 1 } }}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    bgcolor: '#000'
                  }}
                >
                  <iframe
                    key={currentVideo?.videoId || 'NDb5ASZ2t2c'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px'
                    }}
                    src={`https://www.youtube.com/embed/${currentVideo?.videoId || 'NDb5ASZ2t2c'}?vq=hd720&quality=hd720&rel=0&modestbranding=1&fs=1&autoplay=1`}
                    title={currentVideo?.title || "מדריך למתחילים - Costing Wizard"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </Box>
                
                <Box sx={{ mt: 2, px: { xs: 1, sm: 0 } }}>
                  <Box
                    component="h3"
                    sx={{
                      m: 0,
                      mb: 1,
                      color: '#2c3e50',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      fontWeight: 'bold',
                      lineHeight: 1.3
                    }}
                  >
                    {currentVideo?.title || "מדריך למתחילים - Costing Wizard"}
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      color: '#666',
                      m: 0,
                      lineHeight: 1.4
                    }}
                  >
                    משך הסרטון: {currentVideo?.duration || "11:30"} • לחץ על סרטון אחר מהרשימה לצפייה
                  </Box>
                </Box>
              </Box>

              {/* Video Guide Section */}
              <Box sx={{ order: { xs: 1, md: 2 } }}>
                <VideoGuideSection
                  title="מדריכי וידאו מובחרים"
                  videos={videoGuideData}
                  onVideoClick={handleVideoClick}
                  onShowAllClick={handleShowAllVideos}
                  showAllCoursesText="צפה בכל הסרטונים בערוץ שלנו"
                  maxVideos={4}
                  currentVideoId={currentVideo?.videoId}
                />
              </Box>
            </Box>
            
            <div className="highlight-box" style={{ marginTop: '30px' }}>
              <h4 style={{ margin: '0 0 15px 0' }}>💡 טיפים לצפייה יעילה</h4>
              <ul style={{ margin: 0, paddingRight: '20px' }}>
                <li><strong>התחל בסדר</strong> - צפה בסרטונים לפי הסדר המוצע</li>
                <li><strong>תרגל בזמן אמת</strong> - פתח את האפליקציה בזמן הצפייה</li>
                <li><strong>השהה ונסה</strong> - השהה את הסרטון ונסה לבצע בעצמך</li>
                <li><strong>חזור למדריך</strong> - השתמש במדריך הכתוב לפרטים נוספים</li>
              </ul>
            </div>
            
            <div className="success-box" style={{ marginTop: '20px', textAlign: 'center' }}>
              <strong>🎯 סרטונים חדשים מתווספים מידי חודש!</strong><br/>
              עקבו אחר העדכונים כדי ללמוד על תכונות חדשות
            </div>
          </section>

          {/* Getting Started Section */}
          <section id="h2-getting-started-main" className="section">
            <h2 id="h2-getting-started-main">🚀 תחילת עבודה</h2>
            <p>צעדים ראשונים להתחלת עבודה עם CostingWizard</p>
            
            <div className="highlight-box">
              <h4>📱 הורדת האפליקציה</h4>
              <p>ראשית, הורידו את האפליקציה מ-App Store או Google Play והירשמו לחשבון חדש.</p>
            </div>

            <div className="highlight-box">
              <h4>🏗️ יצירת פרויקט ראשון</h4>
              <p>לאחר ההרשמה, צרו את הפרויקט הראשון שלכם - באופן ידני או בעזרת AI.</p>
              
              <div style={{textAlign: 'center', margin: '16px 0 24px 0', display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap'}}>
                <div>
                  <a href="/images/guide/create_new_project.png" target="_blank" rel="noopener noreferrer">
                    <img src="/images/guide/create_new_project.png" alt="מסך רשימת פרויקטים" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                  </a>
                  <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>מסך רשימת פרויקטים</div>
                </div>
                <div>
                  <a href="/images/guide/project_details.png" target="_blank" rel="noopener noreferrer">
                    <img src="/images/guide/project_details.png" alt="פרטי פרויקט חדש" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                  </a>
                  <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>פרטי פרויקט חדש</div>
                </div>
              </div>

            </div>
          </section>

          {/* Add Chapters Items Section */}
          <section id="h2-add-chapters-items" className="section">
            <h2 id="h2-add-chapters-items">🧱 הוספת פרקים וסעיפים לפרויקט</h2>
            <p>לאחר יצירת פרויקט, תוכלו להוסיף פרקים (תחומי עבודה) וסעיפים (משימות ספציפיות).</p>
            
            <div className="feature-list">
              <h4>📋 מהו פרק?</h4>
              <p>פרק הוא קבוצה של עבודות מאותו תחום, לדוגמה:</p>
              <ul>
                <li>עבודות הריסה</li>
                <li>עבודות בנייה</li>
                <li>חשמל ותאורה</li>
                <li>אינסטלציה</li>
              </ul>
              
              <div style={{textAlign: 'center', margin: '16px 0 20px 0'}}>
                <a href={guideImages.saveNewChapter} target="_blank" rel="noopener noreferrer">
                  <img src={guideImages.saveNewChapter} alt="הוספת פרק חדש" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                </a>
                <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>הוספת פרק חדש</div>
              </div>
            </div>

            <div className="feature-list">
              <h4>📝 מהו סעיף?</h4>
              <p>סעיף הוא משימה ספציפית בתוך פרק, לדוגמה:</p>
              <ul>
                <li>התקנת שקעים - 15 יח'</li>
                <li>הנחת כבלים - 50 מטר</li>
                <li>התקנת לוח חשמל - 1 יח'</li>
              </ul>
              
              <div style={{textAlign: 'center', margin: '16px 0 20px 0'}}>
                <a href={guideImages.itemWithoutResources} target="_blank" rel="noopener noreferrer">
                  <img src={guideImages.itemWithoutResources} alt="סעיף ללא משאבים" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                </a>
                <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>סעיף ללא משאבים</div>
              </div>
            </div>
          </section>

          {/* Add Location Quantities Section */}
          <section id="h2-add-location-quantities" className="section">
            <h2 id="h2-add-location-quantities">📍 הוספת מיקום וכמויות לסעיף</h2>
            <p>בשלב זה תוכלו להכניס את המיקום של הסעיף בפרויקט ואת הכמות בפועל, הנקבעת לפי ארבעה מכפילים.</p>
            
            <div className="highlight-box">
              <h4>🧮 איך מחושבת הכמות?</h4>
              <p><strong>כמות = מכפיל 1 × מכפיל 2 × מכפיל 3 × מכפיל 4</strong></p>
              <p>זוהי דרך גמישה ונוחה לחשב כמויות על בסיס מידות או מרכיבים שונים. אם לא צריך את כל המכפילים – פשוט השאירו אותם על 1.0.</p>
            </div>

            <div className="feature-list">
              <h4>💡 דוגמה מעשית</h4>
              <p><strong>סעיף:</strong> פירוק ריצוף בחדר רחצה</p>
              <ul>
                <li><strong>מיקום:</strong> קומה 1, חדר רחצה</li>
                <li><strong>יחידה:</strong> מ"ר</li>
                <li><strong>מחיר ליחידה:</strong> 120 ₪</li>
              </ul>
              <p><strong>מכפילים:</strong></p>
              <ul>
                <li>מכפיל 1 (רוחב החדר): 2.0 מ'</li>
                <li>מכפיל 2 (אורך החדר): 3.0 מ'</li>
                <li>מכפיל 3, 4: לא נדרשים – נשארים על 1.0</li>
              </ul>
              <p><strong>כמות מחושבת:</strong> 2 × 3 × 1 × 1 = 6.0 מ"ר</p>
              <p><strong>עלות כוללת:</strong> 6.0 מ"ר × 120 ₪ = 720 ₪</p>
            </div>
          </section>

          {/* Dashboard Section */}
          <section id="h2-dashboard" className="section">
            <h2 id="h2-dashboard">📊 לוח הבקרה</h2>
            <p>לוח הבקרה הוא מרכז הפיקוד שלך. כאן תמצא את כל הפרויקטים, סטטיסטיקות שימוש ואפשרויות ניהול החשבון.</p>
            
            <div className="feature-list">
              <h4>👤 פרטי מנוי</h4>
              <ul>
                <li>מידע אישי ופרטי חשבון</li>
                <li>סטטוס מנוי נוכחי</li>  
                <li>הגדרות מטבע</li>
                <li>קישורים לניהול מנוי</li>
              </ul>
            </div>

            <div className="feature-list">
              <h4>📈 נתוני שימוש</h4>
              <ul>
                <li>מונה ייצואי PDF ו-Excel החודש</li>
                <li>שימוש בבינה מלאכותית</li>
                <li>מספר פרויקטים שמורים</li>
                <li>התקדמות לעומת מגבלות המנוי</li>
              </ul>
            </div>
          </section>

          {/* AI Features Section */}
          <section id="h2-ai-features" className="section">
            <h2 id="h2-ai-features">🤖 בינה מלאכותית</h2>
            <p>התכונה החדשנית שלנו מאפשרת יצירת פרויקטים באמצעות בינה מלאכותית. פשוט תאר מה אתה רוצה לבנות והמערכת תיצור עבורך רשימת פריטים מפורטת.</p>
            
            <div className="feature-list">
              <h4>✍️ איך להשתמש ב-AI</h4>
              <ul>
                <li>כתב מה אתה רוצה לבנות או לשפץ</li>
                <li>ציין גדלים וכמויות אם יש לך</li>
                <li>ציין סוג עבודה</li>
                <li>בדוק התוצאה</li>
                <li>שנה מחירים וכמויות לפי המציאות</li>
              </ul>
            </div>

            <div className="warning-box">
              <strong>⚠️ חשוב לזכור:</strong> ה-AI נותן הערכה ראשונית בלבד. תמיד בדוק מחירים עם ספקים ובעלי מקצוע לפני הגשת הצעת מחיר סופית.
            </div>
          </section>

          {/* Advanced Features Section */}
          <section id="h2-advanced-features" className="section">
            <h2 id="h2-advanced-features">✨ תכונות מתקדמות</h2>
            <p>תכונות מתקדמות לחיסכון בזמן ויעילות מרבית.</p>
            
            <div className="feature-list">
              <h4>🔄 תכונות חכמות</h4>
              <ul>
                <li><strong>שכפול פרויקטים:</strong> העתק פרויקט קיים כבסיס לחדש</li>
                <li><strong>חישוב אוטומטי:</strong> עדכון סה"כ אוטומטי בשינוי כמויות</li>
                <li><strong>שכפול פרקים מפרויקט אחר:</strong> העתק פרקים שלמים ממקורות אחרים לפרויקט הנוכחי</li>
              </ul>
            </div>
          </section>

          {/* Export Share Section */}
          <section id="h2-export-share" className="section">
            <h2 id="h2-export-share">📄 ייצוא ושיתוף</h2>
            <p>לאחר יצירת הפרויקט, תוכל לייצא אותו במגוון פורמטים מקצועיים ולשתף עם לקוחות ושותפים.</p>
            <div className="feature-list">
              <h4>📋 סוגי ייצוא זמינים</h4>
              <ul>
                <li><strong>ייצוא PDF:</strong> הפקת הצעת מחיר מעוצבת ומסודרת, מוכנה להדפסה או שליחה ללקוח</li>
                <li><strong>ייצוא Excel:</strong> ייצוא כל הפרויקט לקובץ Excel מפורט – כולל כל הפרקים, הסעיפים והמשאבים</li>
              </ul>
            </div>

            <div className="success-box">
              <strong>💡 טיפ:</strong> קובץ האקסל מעולה להצגה ללקוח או לשימוש פנימי - נוח לעריכה, סינון וסיכום עצמי.
            </div>
          </section>

          {/* Subscription Section */}
          <section id="h2-subscription" className="section">
            <h2 id="h2-subscription">💳 ניהול מנוי</h2>
            <p>המערכת מציעה מספר חבילות מנוי להתאמה לכל סוג של משתמש - מפרילאנסר יחיד ועד חברה גדולה.</p>
            
            <div className="feature-list">
              <h4>💰 חבילות מנוי זמינות</h4>
              <ul>
                <li><strong>חינם:</strong> עד 2 פרויקטים, עד 3 ייצוא PDF/Excel, 2 קריאות בינה מלאכותית, תמיכה בסיסית</li>
                <li><strong>Premium:</strong> עד 8 פרויקטים, עד 20 ייצוא PDF/Excel, 10 קריאות AI, תמיכה מועדפת</li>
                <li><strong>Enterprise Plus:</strong> כל התכונות, AI מוגבל ל-50, מותגות מותאמות, תמיכה VIP</li>
              </ul>
            </div>

            <div className="feature-list">
              <h4>⚙️ ניהול המנוי שלך</h4>
              <ul>
                <li>שדרוג/הורדת דרגה בכל עת</li>
                <li>ביטול מנוי דרך פורטל הלקוח</li>
                <li>צפייה בהיסטוריית תשלומים</li>
                <li>הורדת חשבוניות</li>
                <li>שינוי אמצעי תשלום</li>
              </ul>
            </div>
          </section>

          {/* Tips Section / FAQ */}
          <section id="tips" className="section">
            <h2 id="h2-tips">❓ שאלות נפוצות על CostingWizard</h2>
            <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
              מצא תשובות מהירות לשאלות הנפוצות ביותר על השימוש במערכת
            </p>
            
            <div style={{ display: 'grid', gap: '15px', marginTop: '30px' }}>
              {[
                {
                  q: "איך יוצרים פרויקט חדש?",
                  a: "במסך הראשי לחצו על \"הוסף פרויקט חדש\" ליצירה ידנית, או על \"הוסף פרויקט עם AI\" כדי ליצור הצעת מחיר אוטומטית לפי תיאור שתזינו.",
                  icon: "🏗️"
                },
                {
                  q: "מה ההבדל בין פרק לסעיף?",
                  a: "פרק הוא תחום עבודה כללי (כגון: חשמל, ריצוף, אינסטלציה). סעיף הוא משימה בתוך הפרק (למשל: התקנת שקעים, הנחת ריצוף, החלפת צנרת).",
                  icon: "📋"
                },
                {
                  q: "איך מחשבים את כמות הסעיף?",
                  a: "הכמות מחושבת אוטומטית לפי ארבעה מכפילים: לדוגמה: רוחב × אורך × קומה × חדרים. אם לא צריך את כל המכפילים – פשוט משאירים אותם על 1.",
                  icon: "🧮"
                },
                {
                  q: "מה עושים אם אין לי מחירים למשאבים?",
                  a: "ניתן להוסיף סעיף ללא משאבים, ולהזין לו מחיר ליחידה באופן ידני – פתרון מהיר ונוח כשיש מחיר כולל מהספק.",
                  icon: "💰"
                },
                {
                  q: "איך משתמשים נכון ב-AI של האפליקציה?",
                  a: "תנו תיאור כמה שיותר מפורט של הפרויקט: סוג העבודה, שטח, חומרים, דרישות. המערכת תציע מבנה ראשוני של פרקים וסעיפים שתוכלו לערוך, למחוק או להוסיף עליו.",
                  icon: "🤖"
                },
                {
                  q: "האם אפשר לייצא הצעת מחיר?",
                  a: "כן. ניתן לייצא את הפרויקט ל־PDF או Excel דרך מסך ההצעה, ולשלוח או להדפיס בקלות.",
                  icon: "📄"
                },
                {
                  q: "מה קורה כשעוברים את מגבלת המנוי?",
                  a: "תוצג הודעה מתאימה. ניתן לשדרג את המנוי כדי להמשיך להוסיף פרויקטים, לייצא קבצים או להשתמש ב-AI.",
                  icon: "⚠️"
                },
                {
                  q: "האם הנתונים שלי מגובים?",
                  a: "כן. הנתונים נשמרים באופן מאובטח בענן של Google (Firebase), וזמינים מכל מכשיר בו תתחברו עם אותו חשבון.",
                  icon: "☁️"
                },
                {
                  q: "איך עובדים המכפילים של משאב בתוך סעיף?",
                  a: "אם סעיף כולל משאבים – ניתן להגדיר לכל משאב 4 מכפילים. לדוגמה: כמות חומר × מספר שכבות × קירות × ימים. הכמות הכוללת של המשאב תחושב אוטומטית לפי המכפילים, והתמחור ייקבע בהתאם.",
                  icon: "⚙️"
                },
                {
                  q: "האם אפשר לעבוד גם בלי אינטרנט (אוף־ליין)?",
                  a: "כן. ניתן להוסיף ולערוך נתונים גם כשאין חיבור לאינטרנט. ברגע שתתחברו שוב – השינויים יסונכרנו לענן. עם זאת, לא ניתן לייצא ל־Excel או ליצור פרויקט עם AI במצב אוף־ליין.",
                  icon: "📱"
                }
              ].map((item, index) => (
                <div key={index} 
                     className={`faq-card ${openFaqItem === index ? 'expanded' : ''}`}
                     onClick={() => handleFaqClick(index)}
                     style={{
                       background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                       border: '1px solid #e9ecef',
                       borderRadius: '12px',
                       padding: '20px',
                       cursor: 'pointer',
                       transition: 'all 0.3s ease',
                       boxShadow: openFaqItem === index 
                         ? '0 8px 25px rgba(25, 118, 210, 0.15)' 
                         : '0 2px 8px rgba(0,0,0,0.08)',
                       transform: openFaqItem === index ? 'translateY(-2px)' : 'translateY(0)',
                       borderColor: openFaqItem === index ? '#1976d2' : '#e9ecef'
                     }}>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: openFaqItem === index ? '15px' : '0'
                  }}>
                    <div style={{
                      fontSize: '1.5em',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      {item.icon}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '1.1em',
                        fontWeight: '600',
                        color: '#2c3e50',
                        lineHeight: '1.4'
                      }}>
                        {item.q}
                      </h4>
                      
                      {openFaqItem === index && (
                        <div style={{
                          marginTop: '12px',
                          color: '#495057',
                          lineHeight: '1.6',
                          fontSize: '0.95em',
                          animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                          {item.a}
                        </div>
                      )}
                    </div>
                    
                    <div style={{
                      fontSize: '1.2em',
                      color: '#1976d2',
                      transition: 'transform 0.3s ease',
                      transform: openFaqItem === index ? 'rotate(45deg)' : 'rotate(0deg)',
                      flexShrink: 0
                    }}>
                      +
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="highlight-box" style={{ marginTop: '40px', textAlign: 'center' }}>
              <strong>💬 לא מצאת מה שחיפשת?</strong><br/>
              צור איתנו קשר ונשמח לעזור! שלח אימייל לכתובת: support@costingwizard.com
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default UserGuide; 